import { GeometryObject, Point, MultiPolygon } from "geojson";
import { isFunction } from "lodash";
import { ForwardedRef } from "react";
import WKT from "terraformer-wkt-parser";

export const getBoundsZoomLevel = (
  bounds: google.maps.LatLngBounds,
  map: google.maps.Map
) => {
  const mapDim = map.getDiv().getBoundingClientRect();
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

  const lngDiff = ne.lng() - sw.lng();
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

  const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};

export const isPoint = (point: GeometryObject): point is Point =>
  point.type === "Point";

export const getPositionFromLonLat = (lonlat: string) => {
  if (!lonlat) return;
  const point = WKT.parse(lonlat);
  if (!isPoint(point)) return;
  return {
    lat: point.coordinates[1],
    lng: point.coordinates[0],
  };
};

export const isMultiPolygon = (paths: GeometryObject): paths is MultiPolygon =>
  paths.type === "MultiPolygon";

export const getPathsFromBounds = (bounds?: string) => {
  if (!bounds) return;
  const multiPoly = WKT.parse(bounds);
  if (!isMultiPolygon(multiPoly)) return;
  const paths = multiPoly.coordinates.map((a) =>
    a[0].map((c) => new google.maps.LatLng({ lat: c[1], lng: c[0] }))
  );
  // @ts-ignore
  const bbox_array = multiPoly.bbox();
  const map_bounds = new google.maps.LatLngBounds();
  map_bounds.extend({ lng: bbox_array[0], lat: bbox_array[1] });
  map_bounds.extend({ lng: bbox_array[2], lat: bbox_array[3] });

  return { paths, map_bounds };
};

export const setRef = <T>(ref: ForwardedRef<T>, val: T) => {
  if (ref) {
    if (isFunction(ref)) {
      ref(val);
    } else {
      ref.current = val;
    }
  }
};

export const getMarkerIcon = (focused: boolean = false) => {
  const width = focused ? 47 : 40;
  const height = focused ? 60 : 52;

  return {
    url: "/Pin.svg",
    scaledSize: new google.maps.Size(width, height),
    // anchor: new google.maps.Point(size / 2, size / 2), //Was making markers sit lower than lat/lng
  };
};

export const getBoundsFromLonLats = (lonlats: { lonlat: string }[]) => {
  const bounds = new google.maps.LatLngBounds();
  lonlats.forEach(({ lonlat }) => {
    const position = getPositionFromLonLat(lonlat);
    if (position) bounds.extend(position);
  });
  return bounds;
};
